import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Image, ZoomIn } from 'lucide-react';
import surveyService from '../services/surveyService';

const ProductSearch = ({
  value,
  onChange,
  categoria,
  placeholder = 'Buscar produto no estoque...',
  label,
  showImagePreview = true
}) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const { data: productsData, isLoading } = useQuery({
    queryKey: ['budget-sheet-products', categoria, search],
    queryFn: () => surveyService.getAvailableProducts({
      categoria: categoria || undefined,
      search: search || undefined,
      limit: 20
    }),
    enabled: search.length >= 2 || !search
  });

  const products = productsData?.data || [];

  useEffect(() => {
    if (value && !selectedProduct) {
      const found = products.find(p => p.id === parseInt(value));
      if (found) {
        setSelectedProduct(found);
      }
    }
  }, [value, products, selectedProduct]);

  const handleSelect = (product) => {
    setSelectedProduct(product);
    onChange(product.id);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    setSelectedProduct(null);
    onChange('');
    setShowImageModal(false);
  };

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}

      {!selectedProduct ? (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#49943d] focus:border-transparent border-gray-300 text-sm"
          />
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2 border rounded-lg bg-gray-50">
            {selectedProduct.imagem_url ? (
              <button
                type="button"
                onClick={() => setShowImageModal(true)}
                className="relative group"
              >
                <img
                  src={selectedProduct.imagem_url}
                  alt={selectedProduct.produto_nome}
                  className="w-12 h-12 object-cover rounded"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded flex items-center justify-center transition-all">
                  <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ) : (
              <div className="w-12 h-12 flex items-center justify-center bg-gray-200 rounded">
                <Image className="w-6 h-6 text-gray-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 truncate">
                {selectedProduct.produto_nome}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {selectedProduct.cor && `Cor: ${selectedProduct.cor}`}
                {selectedProduct.medida_text && ` - ${selectedProduct.medida_text}`}
                {selectedProduct.estoque_total !== undefined && (
                  <span className={`ml-2 ${selectedProduct.estoque_total > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    Estoque: {selectedProduct.estoque_total}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {isOpen && search.length >= 2 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="p-3 text-sm text-gray-500">Buscando...</div>
          ) : products.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">Nenhum produto encontrado</div>
          ) : (
            products.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => handleSelect(product)}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center gap-2"
              >
                {product.imagem_url ? (
                  <img
                    src={product.imagem_url}
                    alt={product.produto_nome}
                    className="w-10 h-10 object-cover rounded"
                  />
                ) : (
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded flex-shrink-0">
                    <Image className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {product.produto_nome}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {product.cor && `Cor: ${product.cor}`}
                    {product.medida_text && ` - ${product.medida_text}`}
                    {product.preco_venda && ` - R$ ${Number(product.preco_venda).toLocaleString('pt-BR')}`}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}

      {showImageModal && selectedProduct?.imagem_url && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-[100] flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowImageModal(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={selectedProduct.imagem_url}
              alt={selectedProduct.produto_nome}
              className="w-full max-h-[80vh] object-contain rounded-lg"
              style={{ cursor: 'zoom-out' }}
              onClick={() => setShowImageModal(false)}
            />
            <div className="text-center mt-4">
              <p className="text-white font-medium">{selectedProduct.produto_nome}</p>
              {selectedProduct.cor && (
                <p className="text-gray-300 text-sm">Cor: {selectedProduct.cor}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSearch;
